/**
 * Per-bow chrono calibration — Podium Archery / MFJJ measured speeds.
 * Compiled from his 2024-2026 bow reviews and annual speed tests.
 *
 * Offset = measured_fps - ibo_formula_prediction
 * Apply this offset on top of the IBO formula for a calibrated estimate.
 *
 * Sources: youtube.com/@podiumarcher3447 + ArcheryTalk transcription threads.
 * Compiled 2026-06-01.
 */
export interface BowChronoCalibration {
  brand: string;
  model: string;
  year: number;
  /** Mean (measured - IBO-formula-predicted) FPS across all observed tests. */
  offset: number;
  /** Number of chrono tests this offset is averaged from. */
  nTests: number;
  /** Standard deviation of the offset across observed tests (fps). */
  stdDev: number;
}

export const BOW_CHRONO_CALIBRATIONS: ReadonlyArray<BowChronoCalibration> = [
  { brand: 'Mathews', model: 'Lift X 33', year: 2025, offset: -14.7, nTests: 13, stdDev: 7.8 },
  { brand: 'PSE', model: 'Mach 33 DS Carbon (FDS Cam)', year: 2025, offset: -12.1, nTests: 6, stdDev: 0.8 },
  { brand: 'Hoyt', model: 'Alpha X 33', year: 2024, offset: -5.6, nTests: 5, stdDev: 6.6 },
  { brand: 'Mathews', model: 'ARC 34', year: 2026, offset: -16.1, nTests: 5, stdDev: 6.1 },
  { brand: 'Darton', model: 'Consequence', year: 2024, offset: -22.0, nTests: 3, stdDev: 8.4 },
  { brand: 'Hoyt', model: 'Carbon RX-9 Ultra', year: 2025, offset: -12.0, nTests: 3, stdDev: 6.3 },
  { brand: 'Hoyt', model: 'VTM 31', year: 2023, offset: -11.2, nTests: 3, stdDev: 0.4 },
  { brand: 'Bowtech', model: 'Core SR', year: 2024, offset: -16.2, nTests: 2, stdDev: 6.2 },
  { brand: 'Bowtech', model: 'Proven 34 (speed mod)', year: 2025, offset: -11.8, nTests: 2, stdDev: 3.8 },
  { brand: 'Darton', model: 'Sequel 33', year: 2024, offset: -30.4, nTests: 2, stdDev: 0.6 },
  { brand: 'Darton', model: 'Sequel 31', year: 2024, offset: -7.7, nTests: 2, stdDev: 3.1 },
  { brand: 'Darton', model: 'Sequel 35 ST2 (speed mod)', year: 2025, offset: -8.8, nTests: 2, stdDev: 3.8 },
  { brand: 'Elite', model: 'HNTR 33', year: 2025, offset: -8.8, nTests: 2, stdDev: 4.8 },
  { brand: 'Hoyt', model: 'Alpha AX-2 32', year: 2025, offset: -5.7, nTests: 2, stdDev: 1.0 },
  { brand: 'Hoyt', model: 'Alpha AX-2 29', year: 2025, offset: -10.5, nTests: 2, stdDev: 6.5 },
  { brand: 'Mathews', model: 'Lift 29.5', year: 2024, offset: -12.2, nTests: 2, stdDev: 3.2 },
  { brand: 'Prime', model: 'Form', year: 2025, offset: -6.2, nTests: 2, stdDev: 4.2 },
  { brand: 'Xpedition', model: 'XLite 32', year: 2025, offset: -4.2, nTests: 2, stdDev: 4.2 },
  { brand: 'APA', model: 'Black Mamba 33', year: 2024, offset: -12.0, nTests: 1, stdDev: 0.0 },
  { brand: 'Bear', model: 'Whitetail Maxx', year: 2024, offset: -13.0, nTests: 1, stdDev: 0.0 },
  { brand: 'Bowtech', model: 'Honor', year: 2024, offset: -12.0, nTests: 1, stdDev: 0.0 },
  { brand: 'Bowtech', model: 'Virtue', year: 2025, offset: -7.0, nTests: 1, stdDev: 0.0 },
  { brand: 'Bowtech', model: 'Dominator Duo X 38', year: 2025, offset: 0.0, nTests: 1, stdDev: 0.0 },
  { brand: 'Darton', model: 'Consequence 2', year: 2025, offset: -14.0, nTests: 1, stdDev: 0.0 },
  { brand: 'Elite', model: 'Ethos', year: 2024, offset: -14.0, nTests: 1, stdDev: 0.0 },
  { brand: 'Elite', model: 'Allure', year: 2024, offset: -10.0, nTests: 1, stdDev: 0.0 },
  { brand: 'Elite', model: 'Exalt 35', year: 2025, offset: -10.0, nTests: 1, stdDev: 0.0 },
  { brand: 'Elite', model: 'Carbon EON', year: 2025, offset: -8.0, nTests: 1, stdDev: 0.0 },
  { brand: 'Hoyt', model: 'VTM 34', year: 2023, offset: 0.0, nTests: 1, stdDev: 0.0 },
  { brand: 'Hoyt', model: 'Concept X37', year: 2025, offset: 0.0, nTests: 1, stdDev: 0.0 },
  { brand: 'Hoyt', model: 'RX-8 Ultra', year: 2024, offset: -10.9, nTests: 1, stdDev: 0.0 },
  { brand: 'Mathews', model: 'Lift X 29.5', year: 2025, offset: -13.2, nTests: 1, stdDev: 0.0 },
  { brand: 'Mathews', model: 'ARC 30', year: 2026, offset: -5.4, nTests: 1, stdDev: 0.0 },
  { brand: 'Mathews', model: 'Phase 4 29', year: 2023, offset: -11.8, nTests: 1, stdDev: 0.0 },
  { brand: 'PSE', model: 'Mach 30', year: 2024, offset: -7.0, nTests: 1, stdDev: 0.0 },
  { brand: 'PSE', model: 'Decree', year: 2025, offset: -8.0, nTests: 1, stdDev: 0.0 },
  // Sal's own chrono, 2026-07-04 (not MFJJ): 282 fps @10yd / 264.5 @60yd, 498 gr
  // X10 PPH 250 build (FFP-360 vanes), 70#/29". Muzzle ≈285 vs IBO-formula 284.8
  // → delivers full rated IBO through accessories. Decay 93.8% @50yd (engine-consistent).
  { brand: 'PSE', model: 'Sicario FDS 33', year: 2026, offset: 0.0, nTests: 1, stdDev: 0.0 },
  { brand: 'Prime', model: 'RVX 34', year: 2024, offset: -11.0, nTests: 1, stdDev: 0.0 },
  { brand: 'Xpedition', model: 'XLite 29', year: 2024, offset: -11.0, nTests: 1, stdDev: 0.0 },
];

/** Look up calibration for a specific bow. Returns null if no MFJJ chrono data available. */
export function getChronoCalibration(brand: string, model: string, year?: number): BowChronoCalibration | null {
  const matches = BOW_CHRONO_CALIBRATIONS.filter(c => c.brand === brand && c.model === model);
  if (matches.length === 0) return null;
  if (year !== undefined) {
    const exact = matches.find(c => c.year === year);
    if (exact) return exact;
  }
  // Closest year
  return matches.reduce((a, b) => year !== undefined && Math.abs(a.year - year) <= Math.abs(b.year - year) ? a : b);
}
